import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSearch, FaSave, FaTimes, FaImage, FaArrowUp, FaArrowDown
} from 'react-icons/fa';

const HeroGallery = () => {
  const { can } = usePermission();
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    alt_text: '',
    image: null,
    display_order: 0,
    active: true
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  useEffect(() => {
    fetchGalleryImages();
  }, [searchTerm]);

  const fetchGalleryImages = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm
      };

      const response = await axios.get(`${SERVER}/hero-gallery/admin/list`, {
        params,
        withCredentials: true
      });

      setGalleryImages(response.data.gallery_images);
    } catch (error) {
      console.error('Error fetching hero gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingImage) return;
    if (!canEdit && editingImage) return;

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else if (key !== 'image') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingImage) {
        response = await axios.put(`${SERVER}/hero-gallery/admin/${editingImage.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/hero-gallery/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchGalleryImages();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving hero gallery image:', error);
    }
  };

  const handleEdit = async (image) => {
    if (!canEdit) return;
    
    setFormData({
      title: image.title || '',
      alt_text: image.alt_text || '',
      image: null,
      display_order: image.display_order || 0,
      active: image.active
    });
    setEditingImage(image);
    setShowModal(true);
  };

  const handleDelete = async (image) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${image.title}"?`)) {
      try {
        await axios.delete(`${SERVER}/hero-gallery/admin/${image.id}`, {
          withCredentials: true
        });
        fetchGalleryImages();
      } catch (error) {
        console.error('Error deleting hero gallery image:', error);
      }
    }
  };

  const handleToggleActive = async (image) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/hero-gallery/admin/${image.id}`, {
        ...image,
        active: !image.active
      }, {
        withCredentials: true
      });
      fetchGalleryImages();
    } catch (error) {
      console.error('Error toggling image status:', error);
    }
  };

  const handleReorder = async (imageId, direction) => {
    if (!canEdit) return;

    const currentIndex = galleryImages.findIndex(img => img.id === imageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= galleryImages.length) return;

    const reorderedImages = [...galleryImages];
    [reorderedImages[currentIndex], reorderedImages[newIndex]] = [reorderedImages[newIndex], reorderedImages[currentIndex]];

    // Update display_order for affected images
    const imagesToUpdate = reorderedImages.map((img, index) => ({
      id: img.id,
      display_order: index
    }));

    try {
      await axios.put(`${SERVER}/hero-gallery/admin/reorder`, {
        images: imagesToUpdate
      }, {
        withCredentials: true
      });
      fetchGalleryImages();
    } catch (error) {
      console.error('Error reordering images:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      title: '',
      alt_text: '',
      image: null,
      display_order: galleryImages.length,
      active: true
    });
    setEditingImage(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingImage(null);
    setFormData({
      title: '',
      alt_text: '',
      image: null,
      display_order: 0,
      active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Hero Gallery</h2>
          <p className="text-slate-600">Manage images for the hero section gallery</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Image</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Gallery Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {galleryImages.map((image, index) => (
          <div key={image.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
            {/* Image */}
            <div className="h-48 bg-gradient-to-br from-blue-500 to-indigo-600 relative overflow-hidden">
              <img
                src={`${SERVER}${image.image}`}
                alt={image.alt_text || image.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20" />
              
              {/* Order Controls */}
              {canEdit && (
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  <button
                    onClick={() => handleReorder(image.id, 'up')}
                    disabled={index === 0}
                    className={`w-8 h-8 bg-white/90 rounded-full flex items-center justify-center transition-all ${
                      index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:shadow-lg'
                    }`}
                  >
                    <FaArrowUp className="text-gray-700" size={12} />
                  </button>
                  <button
                    onClick={() => handleReorder(image.id, 'down')}
                    disabled={index === galleryImages.length - 1}
                    className={`w-8 h-8 bg-white/90 rounded-full flex items-center justify-center transition-all ${
                      index === galleryImages.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:shadow-lg'
                    }`}
                  >
                    <FaArrowDown className="text-gray-700" size={12} />
                  </button>
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => handleToggleActive(image)}
                  disabled={!canEdit}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    image.active
                      ? 'bg-green-100/90 text-green-800 hover:bg-green-200/90'
                      : 'bg-red-100/90 text-red-800 hover:bg-red-200/90'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  {image.active ? <FaToggleOn /> : <FaToggleOff />}
                  {image.active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{image.title}</h3>
              {image.alt_text && (
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{image.alt_text}</p>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <span className="text-xs text-slate-500">Order: {image.display_order}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(image)}
                    disabled={!canEdit}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(image)}
                    disabled={!canDelete}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {galleryImages.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Images Found</h3>
          <p className="text-slate-600 mb-6">Add your first hero gallery image to get started.</p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            >
              <FaPlus />
              Add Image
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingImage ? 'Edit Gallery Image' : 'Add New Gallery Image'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter image title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Alt Text
                </label>
                <input
                  type="text"
                  value={formData.alt_text}
                  onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter alt text for accessibility"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Image {!editingImage && '*'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                  required={!editingImage}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingImage ? 'Update Image' : 'Add Image'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroGallery;